import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { REGRA_BONIFICACAO as R, cent, limitesFaixa, nivelDe, recPct, redutor } from '../../../src/lib/bonificacaoParceiro';
import type { ResultadoBonificacao } from '../../../src/lib/bonificacaoParceiro';
import { kwh, mil, pc, pc2, reais, reais0 } from './formato';

// "Entenda a conta e as regras", o bloco que abre embaixo do simulador — o mesmo do
// site (vigorenergy.com.br/parceiros), com as cores do VigorHub e sem comparar com
// o plano padrão.

export type Cliente = { id: string; c: number; d: number; nome: string; exemplo?: boolean };

const FX = R.faixas;
// quanto 1.000 kWh rendem em cada faixa, com o desconto padrão; a maior barra ocupa a altura toda
const POR_MIL = FX.map(f => cent(1000 * R.tarifa * (1 - R.padrao / 100) * f[1]));
const MAIOR_MIL = Math.max(...POR_MIL);
const DESCONTOS = [20, 22, 25, 28, 30, 35];
const ate = (i: number) => (FX[i][0] === Infinity ? `${mil(limitesFaixa(i).de)} ou +` : `até ${mil(FX[i][0] === 39999 ? 40000 : FX[i][0])}`);

/** Um termo da conta; o operador vem colado nele, para a linha nunca quebrar entre o "=" e o resultado. */
const Tok: React.FC<{ v: string; leg: string; op?: string; res?: boolean }> = ({ v, leg, op, res }) => (
  <span className="inline-flex items-center gap-2 min-w-0">
    {op ? <span aria-hidden="true" className="text-gray-500 dark:text-gray-400 font-semibold">{op}</span> : null}
    <span className={`inline-grid gap-0.5 rounded-xl px-3 py-2 min-w-0 ${res ? 'bg-primary-dark text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600'}`}>
      <b className="font-semibold tabular-nums">{v}</b>
      <small className={`text-xs leading-snug ${res ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'}`}>{leg}</small>
    </span>
  </span>
);

const Linha: React.FC<{ rot: string; sua?: boolean; children: React.ReactNode }> = ({ rot, sua, children }) => (
  <div className={`grid gap-3 md:grid-cols-[8rem_minmax(0,1fr)] md:items-center rounded-2xl bg-gray-50 dark:bg-gray-900/60 p-4 ${sua ? 'ring-2 ring-primary/45' : ''}`}>
    <span className="font-semibold text-sm leading-tight">
      {rot}
      {sua ? <small className="block text-xs font-semibold text-primary dark:text-primary-light">sua modalidade</small> : null}
    </span>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

const EntendaConta: React.FC<{ res: ResultadoBonificacao; clientes: Cliente[]; fixo: boolean }> = ({ res, clientes, fixo }) => {
  const [sel, setSel] = useState<string | null>(null);
  const fx = res.fx;
  const oks = clientes.map((x, i) => ({ x, i })).filter(({ i }) => res.linhas[i]?.ok);
  const atual = oks.find(({ x }) => x.id === sel) ?? oks[0];
  const l = atual ? res.linhas[atual.i] : null;
  const rotulo = (x: Cliente, i: number) => x.nome.trim() || `Consumidor ${i + 1}`;
  const muted = 'text-gray-500 dark:text-gray-400';

  return (
    <details className="group mt-6 bg-white dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700 rounded-3xl shadow-sm">
      <summary className="flex items-center justify-between gap-4 p-6 md:px-8 cursor-pointer list-none rounded-3xl [&::-webkit-details-marker]:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
        <span>
          <span className="block text-lg font-bold tracking-tight text-gray-900 dark:text-white">Entenda a conta e as regras</span>
          <span className={`block mt-0.5 text-sm ${muted}`}>A conta passo a passo, as faixas, o desconto e quando você recebe.</span>
        </span>
        <ChevronDown className={`w-5 h-5 shrink-0 ${muted} transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-open:rotate-180`} aria-hidden="true" />
      </summary>

      <div className="border-t border-gray-200/60 dark:border-gray-700 p-5 sm:p-6 md:p-8 grid grid-cols-[minmax(0,1fr)] gap-14 text-gray-800 dark:text-gray-100">
        {/* a conta de um consumidor em blocos: fatura, bonificação e como recebe */}
        <section aria-labelledby="ec-conta">
          <h3 id="ec-conta" className="text-xl font-bold tracking-tight">A conta, passo a passo</h3>
          <p className={`mt-1 ${muted} max-w-[65ch]`}>
            A bonificação é calculada uma vez, no mês em que a Vigor aceita o consumidor. A fatura com desconto usa o consumo médio dos últimos 12 meses e
            não muda com bandeira, reajuste ou consumo do mês.
          </p>

          {!fx || !l || !atual ? (
            <p className={`mt-6 text-sm ${muted}`}>Adicione um consumidor com {kwh(R.minimo)} kWh ou mais para ver a conta desenhada.</p>
          ) : (
            <>
              <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Consumidor da conta">
                {oks.map(({ x, i }) => (
                  <button
                    key={x.id}
                    type="button"
                    aria-pressed={x.id === atual.x.id}
                    onClick={() => setSel(x.id)}
                    className={`min-h-10 px-4 rounded-full text-sm font-semibold border transition-[background-color,border-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] ${
                      x.id === atual.x.id ? 'bg-primary-dark border-primary-dark text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:border-primary'
                    }`}
                  >
                    {rotulo(x, i)}
                  </button>
                ))}
              </div>

              <div className="mt-4 grid gap-2.5">
                <Linha rot="Fatura com desconto">
                  <Tok v={`${kwh(l.c)} kWh`} leg="consumo médio por mês" />
                  <Tok op="×" v={reais(R.tarifa)} leg="por kWh, tarifa da regra" />
                  <Tok op="×" v={pc(1 - l.d / 100)} leg={`paga, com ${l.d}% de desconto`} />
                  <Tok op="=" v={reais(l.F)} leg="por mês" res />
                </Linha>

                <Linha rot="Bonificação">
                  <Tok v={reais(l.F)} leg="fatura com desconto" />
                  <Tok op="×" v={pc(fx.pct)} leg={`faixa F${fx.i + 1}: o mês soma ${kwh(res.V)} kWh`} />
                  {l.d > R.padrao ? <Tok op="×" v={pc(redutor(l.d))} leg={`redutor (${l.d}% de desconto)`} /> : null}
                  <Tok op="=" v={reais(l.B)} leg="bonificação deste consumidor" res />
                </Linha>

                <Linha rot="Na Fixa" sua={fixo}>
                  <div className="w-full grid gap-2">
                    <div className="flex h-3 rounded-full overflow-hidden" aria-hidden="true">
                      <i className="bg-secondary" style={{ width: `${(l.E / l.B) * 100}%` }} />
                      <i className="bg-primary" style={{ width: `${(l.S / l.B) * 100}%` }} />
                    </div>
                    <div className={`flex flex-wrap gap-x-5 gap-y-1 text-sm ${muted}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <i className="w-2.5 h-2.5 rounded-full bg-secondary" aria-hidden="true" />
                        Pix I <b className="text-gray-900 dark:text-white tabular-nums">{reais(l.E)}</b> ({pc(R.entrada)})
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <i className="w-2.5 h-2.5 rounded-full bg-primary" aria-hidden="true" />
                        Pix II <b className="text-gray-900 dark:text-white tabular-nums">{reais(l.S)}</b> ({pc(1 - R.entrada)})
                      </span>
                    </div>
                  </div>
                </Linha>

                <Linha rot="Na Variável" sua={!fixo}>
                  <Tok v={reais(l.E)} leg="Pix I, igual à Fixa" />
                  <span className={`text-sm ${muted} px-1`}>e depois</span>
                  <Tok v={reais(l.F)} leg="cada fatura paga" />
                  <Tok op="×" v={pc2(recPct(l.d))} leg={l.d > R.padrao ? `${pc(R.recBase)} × redutor` : 'de cada fatura paga'} />
                  <Tok op="=" v={reais(l.P)} leg={`todo mês que ele pagar, por ${R.recorrenteAnos} anos`} res />
                </Linha>
              </div>
              <p className={`mt-3 text-sm ${muted} max-w-[65ch]`}>
                {reais(R.tarifa)} é a tarifa média que a Vigor usa só para esta regra. A conta real do consumidor muda todo mês. Na Variável, o recorrente em
                reais é uma estimativa feita com a fatura com desconto: o valor real segue a fatura paga.
              </p>
            </>
          )}
        </section>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-14 lg:grid-cols-2 lg:gap-12">
          {/* faixas: quanto 1.000 kWh rendem em cada uma */}
          <section aria-labelledby="ec-faixas">
            <h3 id="ec-faixas" className="text-xl font-bold tracking-tight">Quanto rende cada 1.000 kWh</h3>
            <p className={`mt-1 ${muted}`}>Em cada faixa do mês, com {R.padrao}% de desconto.</p>
            {/* celular: uma linha por faixa, a barra cresce com o valor */}
            <ul className="mt-6 grid gap-2.5 sm:hidden">
              {POR_MIL.map((v, i) => (
                <li key={i} className="grid grid-cols-[4.75rem_minmax(0,1fr)_4.5rem] items-center gap-3">
                  <span className="grid leading-tight">
                    <b className="text-sm">F{i + 1} · {pc(FX[i][1])}</b>
                    <span className={`text-[11px] ${muted}`}>{ate(i)}</span>
                  </span>
                  <i className={`block h-3 rounded-full ${fx?.i === i ? 'bg-secondary' : 'bg-primary/85'}`} style={{ width: `${(v / MAIOR_MIL) * 100}%` }} />
                  <b className={`text-sm text-right tabular-nums ${fx?.i === i ? 'text-secondary-dark dark:text-secondary' : ''}`}>
                    {reais0(v)}
                    {fx?.i === i ? <span className="sr-only"> (sua faixa)</span> : null}
                  </b>
                </li>
              ))}
            </ul>
            <div className="mt-6 relative hidden sm:block">
              <div className="relative h-44 border-b border-gray-200 dark:border-gray-600">
                <div className="absolute inset-0 grid grid-cols-6 gap-2 sm:gap-3 items-end px-1">
                  {POR_MIL.map((v, i) => (
                    <i key={i} className={`block rounded-t-md transition-colors duration-300 ${fx?.i === i ? 'bg-secondary' : 'bg-primary/85'}`} style={{ height: `${(v / MAIOR_MIL) * 100}%` }} />
                  ))}
                </div>
              </div>
              <ul className="grid grid-cols-6 gap-2 sm:gap-3 px-1 mt-2 text-center">
                {POR_MIL.map((v, i) => (
                  <li key={i} className="grid leading-tight min-w-0">
                    <b className={`text-sm lg:text-xs tabular-nums ${fx?.i === i ? 'text-secondary-dark dark:text-secondary' : ''}`}>{reais0(v)}</b>
                    <span className="text-xs font-semibold mt-0.5">F{i + 1} · {pc(FX[i][1])}</span>
                    <span className={`text-[11px] ${muted}`}>{ate(i)}</span>
                    {fx?.i === i ? <span className="sr-only">sua faixa</span> : null}
                  </li>
                ))}
              </ul>
            </div>
            <p className={`mt-4 text-sm ${muted}`}>A faixa é a soma do consumo dos consumidores aceitos no mês e recomeça a cada mês. O percentual vale para todos eles.</p>
          </section>

          {/* desconto: de cada 10 moedas da bonificação, quantas ficam com você */}
          <section aria-labelledby="ec-desconto">
            <h3 id="ec-desconto" className="text-xl font-bold tracking-tight">Quanto menor o desconto, maior a bonificação</h3>
            <p className={`mt-1 ${muted}`}>De cada 10 moedas da bonificação, quantas ficam com você.</p>
            <ul className="mt-6 grid gap-3.5">
              {DESCONTOS.map(d => {
                const r = redutor(d);
                return (
                  <li key={d} className="grid grid-cols-[3rem_minmax(0,1fr)] sm:grid-cols-[3rem_auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1">
                    <span className="font-bold tabular-nums">{d}%</span>
                    <span className="flex gap-1" aria-hidden="true">
                      {Array.from({ length: 10 }, (_, k) => {
                        const f = Math.max(0, Math.min(1, r * 10 - k));
                        return <i key={k} className="w-3.5 h-3.5 rounded-full border border-secondary" style={{ background: `linear-gradient(90deg, #F6A937 ${f * 100}%, transparent ${f * 100}%)` }} />;
                      })}
                    </span>
                    <span className="col-start-2 sm:col-start-3 text-sm leading-tight min-w-0">
                      <b className={d === R.padrao ? 'text-primary dark:text-primary-light' : 'text-secondary-dark dark:text-secondary'}>fica {pc(r)}</b>
                      <small className={`ml-2 sm:ml-0 sm:block text-xs ${muted}`}>
                        {nivelDe(d)?.n} · {d === R.padrao ? 'padrão, sem aprovação' : 'com aprovação'}
                      </small>
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className={`mt-5 text-sm ${muted}`}>
              De 21% a {R.meio}%, cada ponto de desconto tira {R.perda1} pontos. Acima disso, {R.perda2} pontos.
            </p>
            <p className={`mt-2 text-sm ${muted}`}>
              Desconto máximo pelo consumo:{' '}
              {R.segmentos
                .map(([teto, , max], i) => {
                  const de = i ? R.segmentos[i - 1][0] + 1 : R.minimo;
                  return `até ${max}% ${teto === Infinity ? `a partir de ${kwh(de)} kWh` : `de ${kwh(de)} a ${kwh(teto)} kWh`}`;
                })
                .join(', ')
                .replace(/, ([^,]*)$/, ' e $1')}
              .
            </p>
          </section>
        </div>

        <section aria-labelledby="ec-regras">
          <h3 id="ec-regras" className="text-xl font-bold tracking-tight">As regras que mudam o valor</h3>
          <div className="mt-6 grid gap-10 md:grid-cols-3 md:gap-8">
            <div>
              <h4 className="font-semibold">Quem conta para a faixa</h4>
              <ul className={`mt-3 grid gap-2 text-sm ${muted} list-disc pl-5 marker:text-primary`}>
                <li>Cada consumidor precisa ter pelo menos {kwh(R.minimo)} kWh por mês.</li>
                <li>Só Grupo B (B1, B2 e B3). Grupo A, Grupo A optante B e tarifa social não podem assinar.</li>
                <li>A venda só conta depois da assinatura do titular, da análise de cadastro e da ligação de confirmação da Vigor.</li>
                <li>O mesmo consumidor (CPF, CNPJ ou unidade consumidora) não gera bonificação nova antes de 24 meses.</li>
                <li>Se você só indicar e a Vigor fizer a venda, recebe {reais0(R.indicacao)} por consumidor fechado, no lugar da bonificação.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">Quando você recebe</h4>
              <ul className={`mt-3 grid gap-2 text-sm ${muted} list-disc pl-5 marker:text-primary`}>
                <li>
                  <b className="text-gray-900 dark:text-white">Fixa:</b> Pix I no dia 10 do mês seguinte ao aceite. Pix II no dia 10 do mês seguinte à 1ª fatura
                  paga, mesmo se o consumidor pagar com atraso.
                </li>
                <li>
                  <b className="text-gray-900 dark:text-white">Variável:</b> o mesmo Pix I. Depois, todo mês em que o consumidor pagar, {pc(R.recBase)} do valor
                  pago vezes o redutor, por {R.recorrenteAnos} anos depois da assinatura.
                </li>
                <li>Você escolhe Fixa ou Variável no contrato, e ela vale para todos os seus consumidores.</li>
                <li>A Vigor paga uma vez por mês, contra nota fiscal da sua empresa. Total abaixo de {reais0(R.pagamentoMinimo)} passa para o mês seguinte.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">Quando o valor muda</h4>
              <ul className={`mt-3 grid gap-2 text-sm ${muted} list-disc pl-5 marker:text-primary`}>
                <li>Fatura não paga não gera pagamento. Fatura negociada conta quando for paga.</li>
                <li>1ª fatura sem pagamento em 60 dias: o Pix I sai das suas próximas bonificações, até metade de cada pagamento. Se o consumidor pagar depois, ele volta.</li>
                <li>Na Variável, se o consumidor sair cedo, você pode ganhar menos que na Fixa.</li>
                <li>Fraude, aliciamento ou descumprimento do contrato encerram os pagamentos.</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </details>
  );
};

export default EntendaConta;
